import { CheckCircle2, Circle } from 'lucide-react';
import type { PipelineRound } from '../../types';

interface TimelineStepProps {
  round: PipelineRound;
  isCurrent: boolean;
  isCompleted: boolean;
  isLast: boolean;
  isClickable: boolean;
  onClick: () => void;
  layout?: 'horizontal' | 'vertical';
}

export function TimelineStep({
  round,
  isCurrent,
  isCompleted,
  isLast,
  isClickable,
  onClick,
  layout = 'horizontal',
}: TimelineStepProps) {
  if (layout === 'vertical') {
    return (
      <div className="flex items-start gap-3 relative">
        {/* Left: Circle and connecting line */}
        <div className="flex flex-col items-center">
          {/* Circle/Dot */}
          <button
            onClick={onClick}
            disabled={!isClickable}
            className={`
              relative z-10 flex-shrink-0
              ${isClickable ? 'cursor-pointer hover:scale-110' : 'cursor-default'}
              transition-transform duration-200
              ${!isClickable && !isCurrent && !isCompleted && 'opacity-50'}
            `}
          >
            <div
              className={`
                w-8 h-8 rounded-full flex items-center justify-center
                border-2 transition-all duration-200
                ${
                  isCurrent
                    ? 'bg-green-500 border-green-600 shadow-lg ring-4 ring-green-100 w-9 h-9'
                    : isCompleted
                      ? 'bg-blue-500 border-blue-600'
                      : 'bg-white border-gray-300'
                }
              `}
            >
              {isCurrent ? (
                <CheckCircle2 className="w-5 h-5 text-white" />
              ) : isCompleted ? (
                <Circle className="w-4 h-4 text-white fill-white" />
              ) : (
                <Circle className="w-4 h-4 text-gray-400" />
              )}
            </div>
          </button>

          {/* Vertical connecting line (if not last) */}
          {!isLast && (
            <div
              className={`
                w-0.5 my-1
                ${isCompleted ? 'bg-blue-500' : 'bg-gray-300'}
              `}
              style={{ minHeight: '32px' }}
            />
          )}
        </div>

        {/* Right: Label and details */}
        <div className="flex-1 pb-6 pt-1 min-w-0">
          <button
            onClick={onClick}
            disabled={!isClickable}
            className={`
              text-left block w-full
              ${isClickable ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}
              transition-opacity duration-200
            `}
            title={round.name}
          >
            <div
              className={`
                text-sm font-medium overflow-hidden
                ${
                  isCurrent
                    ? 'font-bold text-green-700'
                    : isCompleted
                      ? 'text-blue-600'
                      : 'text-gray-600'
                }
              `}
              style={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: '1.4',
              }}
            >
              {round.name}
            </div>
          </button>
        </div>
      </div>
    );
  }

  // Horizontal layout (original)
  return (
    <div className="flex items-center relative">
      {/* Step circle */}
      <button
        onClick={onClick}
        disabled={!isClickable}
        className={`
          relative z-10 flex flex-col items-center
          ${isClickable ? 'cursor-pointer hover:scale-105' : 'cursor-default'}
          transition-transform duration-200
          ${!isClickable && !isCurrent && !isCompleted && 'opacity-50'}
        `}
      >
        {/* Circle/Dot */}
        <div
          className={`
            w-7 h-7 rounded-full flex items-center justify-center
            border-2 transition-all duration-200
            ${
              isCurrent
                ? 'bg-green-500 border-green-600 shadow-lg ring-4 ring-green-100 h-8 w-8'
                : isCompleted
                  ? 'bg-blue-500 border-blue-600'
                  : 'bg-white border-gray-300'
            }
          `}
        >
          {isCurrent ? (
            <CheckCircle2 className="w-5 h-5 text-white" />
          ) : isCompleted ? (
            <Circle className="w-4 h-4 text-white fill-white" />
          ) : (
            <Circle className="w-4 h-4 text-gray-400" />
          )}
        </div>

        {/* Label */}
        <div
          className={`
            mt-2 text-xs text-center max-w-[60px] sm:max-w-[70px]
            min-h-[32px] overflow-hidden
            ${
              isCurrent
                ? 'font-bold text-green-700'
                : isCompleted
                  ? 'text-blue-600'
                  : 'text-gray-500'
            }
          `}
          title={round.name}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            lineHeight: '1.3',
          }}
        >
          {round.name}
        </div>
      </button>

      {/* Connecting line (if not last) */}
      {!isLast && (
        <div
          className={`
            flex-1 h-0.5 mx-1 sm:mx-2
            ${isCompleted ? 'bg-blue-500' : 'bg-gray-300'}
          `}
          style={{ minWidth: '20px', maxWidth: '50px' }}
        />
      )}
    </div>
  );
}
